import { Head, router } from '@inertiajs/react'
import { CardElement, Elements, PaymentRequestButtonElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import { useEffect, useMemo, useRef, useState } from 'react'
import Button from '../../Components/Button'
import Modal from '../../Components/Modal'
import EmptyState from '../../Components/EmptyState'
import FormInput from './Components/FormInput'
import OrderInfoCard from './Components/OrderInfoCard'
import OrderSummary from './Components/OrderSummary'
import TableSelector from './Components/TableSelector'
import UpsellBanner from './Components/UpsellBanner'
import Layout from './Layout'
import { formatPrice, formatTime, generateTimeOptions } from './utils/formatters'
import { parseKey } from './utils/basketKeys'

function CheckoutForm({ account, basket, setBasket, pendingUpsell, setPendingUpsell, pickup_time, table_number, table_source, source, initialCustomerNotes, onSuccess, skipStripe, upsell, indoor_tables }) {
  const stripe = useStripe()
  const elements = useElements()
  const [email, setEmail] = useState('')
  const [emailError, setEmailError] = useState('')
  const [phone, setPhone] = useState('')
  const [marketingConsent, setMarketingConsent] = useState(false)
  const [customerNotes, setCustomerNotes] = useState(initialCustomerNotes || '')
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState(null)
  const [paymentRequest, setPaymentRequest] = useState(null)
  const [currentTableNumber, setCurrentTableNumber] = useState(table_number)
  const [currentTableSource, setCurrentTableSource] = useState(table_source)
  const [currentPickupTime, setCurrentPickupTime] = useState(pickup_time)
  const [showTableModal, setShowTableModal] = useState(false)
  const [showTimeModal, setShowTimeModal] = useState(false)

  // Refs to track latest customerNotes/marketingConsent for Apple Pay closure
  const customerNotesRef = useRef(customerNotes)
  useEffect(() => {
    customerNotesRef.current = customerNotes
  }, [customerNotes])
  const marketingConsentRef = useRef(marketingConsent)
  useEffect(() => {
    marketingConsentRef.current = marketingConsent
  }, [marketingConsent])

  // Upsells added on checkout are kept out of `basket` so handleBack can't
  // leak them into the session basket and resurrect them on a return trip.
  const mergedBasket = useMemo(() => {
    if (!pendingUpsell) return basket
    const key = String(pendingUpsell.id)
    if (basket[key]) return basket
    return { ...basket, [key]: pendingUpsell }
  }, [basket, pendingUpsell])

  const basketTotal = useMemo(() => {
    return Object.values(mergedBasket).reduce((sum, item) => {
      // Enriched lines carry unit_price_pence (base + add-on deltas); the
      // checkout upsell is a plain line with only price_pence.
      const unitPrice = item.unit_price_pence ?? item.price_pence
      return sum + (unitPrice * item.quantity)
    }, 0)
  }, [mergedBasket])

  // Set up Apple Pay / Google Pay
  useEffect(() => {
    if (skipStripe || !stripe || !basketTotal) return

    const pr = stripe.paymentRequest({
      country: 'GB',
      currency: 'gbp',
      total: {
        label: `${account.name} Order`,
        amount: basketTotal,
      },
      requestPayerName: false,
      requestPayerEmail: true,
      requestPayerPhone: false,
    })

    // Check if Apple Pay or Google Pay is available
    pr.canMakePayment().then(result => {
      if (result) {
        setPaymentRequest(pr)
      }
    })

    // Handle payment
    pr.on('paymentmethod', async (ev) => {
      setProcessing(true)
      setError(null)

      try {
        const orderItems = Object.entries(mergedBasket).map(([key, item]) => {
          // The composite key carries the chosen add-on ids; the server prices
          // and snapshots them (client prices are ignored). The upsell line has
          // a plain key, so its add_on_ids is [].
          const { menuItemId, addOnIds } = parseKey(key)
          return {
            menu_item_id: menuItemId,
            quantity: item.quantity,
            add_on_ids: addOnIds
          }
        })

        // Create order (simplified - actual payment confirmation happens server-side)
        router.post(`/${account.slug}/order/create`, {
          order: {
            email: ev.payerEmail,
            phone: ev.payerPhone || '',
            pickup_time: currentPickupTime || new Date(Date.now() + 30 * 60000).toISOString(),
            total_pence: basketTotal,
            stripe_payment_id: ev.paymentMethod.id,
            status: 'paid',
            customer_notes: customerNotesRef.current,
            marketing_consent: marketingConsentRef.current,
            order_items_attributes: orderItems
          },
          source,
          table_source: currentTableSource,
          table_number: currentTableNumber
        }, {
          onSuccess: () => {
            ev.complete('success')
          },
          onError: (errors) => {
            ev.complete('fail')
            setError(errors.message || 'Payment failed')
            setProcessing(false)
          }
        })
      } catch (err) {
        ev.complete('fail')
        setError(err.message)
        setProcessing(false)
      }
    })
  }, [stripe, basketTotal, mergedBasket, account])

  // Email validation
  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return re.test(email)
  }

  const handleEmailChange = (e) => {
    const value = e.target.value
    setEmail(value)
    if (value && !validateEmail(value)) {
      setEmailError('Please enter a valid email address')
    } else {
      setEmailError('')
    }
  }

  const handleUpdateQuantity = (itemId, newQuantity) => {
    if (pendingUpsell && String(pendingUpsell.id) === String(itemId)) {
      if (newQuantity <= 0) {
        setPendingUpsell(null)
      } else {
        setPendingUpsell(prev => prev && { ...prev, quantity: newQuantity })
      }
      return
    }

    if (newQuantity === 0) {
      setBasket(prev => {
        const { [itemId]: _removed, ...rest } = prev
        return rest
      })
    } else {
      setBasket(prev => ({
        ...prev,
        [itemId]: {
          ...prev[itemId],
          quantity: newQuantity
        }
      }))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    // Validate email
    if (!email || !validateEmail(email)) {
      setEmailError('Please enter a valid email address')
      return
    }

    if (!skipStripe && (!stripe || !elements)) return

    setProcessing(true)
    setError(null)

    try {
      let paymentMethodId

      if (skipStripe) {
        paymentMethodId = 'pm_e2e_test_bypass'
      } else {
        // Get card element
        const cardElement = elements.getElement(CardElement)

        // Create payment method with UK billing details
        const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
          type: 'card',
          card: cardElement,
          billing_details: {
            email,
            address: {
              country: 'GB'
            }
          }
        })

        if (pmError) {
          throw new Error(pmError.message)
        }

        paymentMethodId = paymentMethod.id
      }

      // Create order with payment method
      const orderItems = Object.entries(mergedBasket).map(([key, item]) => {
        // The composite key carries the chosen add-on ids; the server prices
        // and snapshots them (client prices are ignored). The upsell line has
        // a plain key, so its add_on_ids is [].
        const { menuItemId, addOnIds } = parseKey(key)
        return {
          menu_item_id: menuItemId,
          quantity: item.quantity,
          add_on_ids: addOnIds
        }
      })

      router.post(`/${account.slug}/order/create`, {
        order: {
          email,
          phone,
          pickup_time: currentPickupTime || new Date(Date.now() + 30 * 60000).toISOString(),
          total_pence: basketTotal,
          stripe_payment_id: paymentMethodId,
          status: 'paid',
          customer_notes: customerNotes,
          marketing_consent: marketingConsent,
          order_items_attributes: orderItems
        },
        source: new URLSearchParams(window.location.search).get('source'),
        table_source: currentTableSource,
        table_number: currentTableNumber
      }, {
        onSuccess: () => {
          // Inertia will redirect to confirmation
        },
        onError: (errors) => {
          setError(errors.message || 'Payment failed')
          setProcessing(false)
        }
      })
    } catch (err) {
      setError(err.message)
      setProcessing(false)
    }
  }

  const isUpsellInBasket = upsell && (
    !!basket[String(upsell.menu_item.id)] || !!pendingUpsell
  )

  const handleAddUpsell = (menuItem) => {
    setPendingUpsell(prev =>
      prev
        ? { ...prev, quantity: prev.quantity + 1 }
        : { ...menuItem, quantity: 1 }
    )
  }

  const isEmpty = Object.keys(mergedBasket).length === 0

  if (isEmpty) {
    return (
      <EmptyState
        emoji="🛒"
        title="Your basket is empty"
        description="You removed all items. Add some items to continue."
        actionLabel="Start Ordering"
        onAction={() => router.visit(`/${account.slug}/order/food`)}
      />
    )
  }

  return (
    <form onSubmit={handleSubmit} className="px-4 py-6 max-w-md mx-auto" aria-label="Checkout form">
      {currentTableNumber ? (
        <div className="mb-6">
          <OrderInfoCard subtitle="Delivering to" onEdit={() => setShowTableModal(true)}>
            Table {currentTableNumber}
          </OrderInfoCard>
        </div>
      ) : (
        <div className="mb-6">
          <OrderInfoCard subtitle="Ready at" onEdit={() => setShowTimeModal(true)}>
            {currentPickupTime || 'ASAP'}
          </OrderInfoCard>
        </div>
      )}

      {showTableModal && (
        <Modal title="Change Table" onClose={() => setShowTableModal(false)}>
          <TableSelector
            tables={indoor_tables || []}
            value={currentTableSource}
            onSelect={(selected) => {
              if (selected) {
                setCurrentTableNumber(selected.table_number)
                setCurrentTableSource(selected.qr_code)
              }
            }}
          />
          <Button
            type="button"
            onClick={() => setShowTableModal(false)}
            variant="primary"
            size="lg"
            fullWidth
            className="mt-4 rounded-xl"
          >
            Save
          </Button>
        </Modal>
      )}

      {showTimeModal && (
        <Modal title="Change Pickup Time" onClose={() => setShowTimeModal(false)}>
          <select
            value={currentPickupTime || ''}
            onChange={(e) => setCurrentPickupTime(e.target.value)}
            className="w-full px-4 py-3 rounded-lg border border-gray-300 text-gray-900 font-medium text-center focus:outline-none focus:ring-2 focus:ring-sky-500"
            aria-label="Select pickup time"
          >
            {generateTimeOptions().map((time) => (
              <option key={time} value={time}>{time}</option>
            ))}
          </select>
          <Button
            type="button"
            onClick={() => setShowTimeModal(false)}
            variant="primary"
            size="lg"
            fullWidth
            className="mt-4 rounded-xl"
          >
            Save
          </Button>
        </Modal>
      )}

      <UpsellBanner
        upsell={upsell}
        isInBasket={isUpsellInBasket}
        onAdd={handleAddUpsell}
      />
      <div className="mb-6">
        <OrderSummary
          basket={mergedBasket}
          total={basketTotal}
          onUpdateQuantity={handleUpdateQuantity}
          editable={true}
        />
      </div>

      {/* Customer notes */}
      <div className="mb-6">
        <label htmlFor="checkout-notes" className="block text-white text-sm font-medium mb-2">
          Special Requests or Dietary Requirements (optional)
        </label>
        <textarea
          id="checkout-notes"
          value={customerNotes}
          onChange={(e) => setCustomerNotes(e.target.value)}
          placeholder="E.g., no onions, gluten allergy, extra sauce..."
          maxLength={500}
          rows={3}
          aria-describedby="checkout-notes-count"
          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent text-gray-900"
        />
        <p id="checkout-notes-count" className="mt-1 text-xs text-white/70">
          {customerNotes.length}/500 characters
        </p>
      </div>

      {/* Apple Pay / Google Pay button */}
      {!skipStripe && paymentRequest && (
        <div className="mb-4">
          <PaymentRequestButtonElement options={{paymentRequest}} />
        </div>
      )}

      {!skipStripe && paymentRequest && (
        <div className="relative mb-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-white/30"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-gradient-to-br from-sky-800 via-sky-700 to-sky-900 text-white/70">
              Or pay with card
            </span>
          </div>
        </div>
      )}

      <div className="mb-4">
        <FormInput
          label="Email (for confirmation)"
          type="email"
          id="checkout-email"
          value={email}
          onChange={handleEmailChange}
          placeholder="you@example.com"
          required
          aria-describedby={emailError ? 'email-error' : undefined}
          aria-invalid={emailError ? 'true' : undefined}
        />
        {emailError && (
          <p id="email-error" className="mt-1 text-sm text-red-300" role="alert">
            {emailError}
          </p>
        )}
      </div>

      <FormInput
        label="Phone Number (optional)"
        type="tel"
        id="checkout-phone"
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
        placeholder="07123456789"
        className="mb-4"
      />

      <label className="flex items-start gap-3 mb-6 text-sm text-white/90 cursor-pointer">
        <input
          type="checkbox"
          checked={marketingConsent}
          onChange={(e) => setMarketingConsent(e.target.checked)}
          className="mt-0.5 h-5 w-5 rounded border-gray-300 text-sky-600 focus:ring-sky-500 flex-shrink-0"
        />
        <span>Email me news and offers from {account.name}. Unsubscribe any time.</span>
      </label>

      {!skipStripe && (
        <div className="mb-6">
          <label id="card-details-label" className="block text-white text-sm font-medium mb-2">
            Card Details
          </label>
          <div className="bg-white px-4 py-3 rounded-lg min-h-[44px]" aria-labelledby="card-details-label">
            {!stripe ? (
              <div className="text-gray-500 text-sm" role="status">Loading payment form...</div>
            ) : (
              <CardElement
                options={{
                  hidePostalCode: true, // UK cards don't need ZIP
                  style: {
                    base: {
                      fontSize: '16px',
                      color: '#424770',
                      '::placeholder': {
                        color: '#aab7c4',
                      },
                    },
                    invalid: {
                      color: '#9e2146',
                    },
                  },
                }}
              />
            )}
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-500 text-white px-4 py-3 rounded-lg mb-4" role="alert">
          {error}
        </div>
      )}

      <div aria-live="polite" className="sr-only">
        {processing ? 'Processing your payment, please wait.' : ''}
      </div>

      <Button
        type="submit"
        disabled={(!skipStripe && !stripe) || processing}
        variant="secondary"
        theme="dark"
        size="lg"
        fullWidth
        className="rounded-xl"
        ariaLabel={processing ? 'Processing payment' : `Pay ${formatPrice(basketTotal)}`}
      >
        {processing ? 'Processing...' : `Pay ${formatPrice(basketTotal)}`}
      </Button>
    </form>
  )
}

export default function Checkout({ account, basket: initialBasket, stripe_key, pickup_time, customer_notes, table_number, table_source, source, skip_stripe, upsell, indoor_tables }) {
  const [basket, setBasket] = useState(initialBasket)
  // Tracked separately from `basket` so an Edit-Order trip can't persist
  // an on-checkout upsell into the session.
  const [pendingUpsell, setPendingUpsell] = useState(null)

  const stripePromise = useMemo(() => {
    if (skip_stripe || !stripe_key) return null
    return loadStripe(stripe_key)
  }, [stripe_key, skip_stripe])

  const handleBack = () => {
    // Only the real basket is synced back to the session; pendingUpsell is
    // intentionally dropped so it can't reappear on the next checkout visit.
    const simplifiedBasket = Object.fromEntries(
      Object.entries(basket).map(([id, item]) => [id, item.quantity])
    )

    router.post(`/${account.slug}/order/update-basket`, {
      basket: simplifiedBasket,
      next_page: 'drinks'
    })
  }

  const isEmpty = (!basket || Object.keys(basket).length === 0) && !pendingUpsell

  return (
    <>
      <Head title={`Checkout - ${account.name}`} />
      <Layout account={account}>
        <div className="min-h-screen">
        <nav className="sticky top-0 z-40 bg-sky-900 text-white px-4 py-6" aria-label="Checkout navigation">
          <div className="flex items-center justify-between">
            <button
              onClick={handleBack}
              className="text-white hover:text-white/80 flex items-center gap-2 focus:outline-none focus:ring-2 focus:ring-white rounded px-2 py-1"
              aria-label="Go back to edit order"
            >
              <span aria-hidden="true">←</span> Edit Order
            </button>
            <h1 className="text-2xl font-bold">Checkout</h1>
            <div className="w-20" aria-hidden="true"></div>
          </div>
        </nav>

        {isEmpty ? (
          <EmptyState
            emoji="🛒"
            title="Your basket is empty"
            description="Add some items to your basket before checking out"
            actionLabel="Start Ordering"
            onAction={() => router.visit(`/${account.slug}/order/food`)}
          />
        ) : (
          <Elements stripe={stripePromise}>
            <CheckoutForm account={account} basket={basket} setBasket={setBasket} pendingUpsell={pendingUpsell} setPendingUpsell={setPendingUpsell} pickup_time={pickup_time} table_number={table_number} table_source={table_source} source={source} initialCustomerNotes={customer_notes} skipStripe={skip_stripe} upsell={upsell} indoor_tables={indoor_tables} />
          </Elements>
        )}
        </div>
      </Layout>
    </>
  )
}
